/**
 * Client types for Batch 5 (Close & Trust the Books): fiscal periods (IBA-72), opening balances (IBA-71),
 * and account reconciliation (IBA-73). Money stays a canonical decimal STRING from the wire to the renderer
 * (IBA-116) — see `MoneyText`. The api layer no longer coerces it to a float.
 */
import type { CurrencyCode, FinanceCategoryScope, FinancePaymentMethod, MoneyText } from "@/features/finance/model/finance-types"

/* --- Fiscal periods (IBA-72) --- */

export enum FiscalPeriodStatus {
  Open = "OPEN",
  Closed = "CLOSED",
}

export interface FiscalPeriod {
  id: string
  sequence: number
  label: string
  startDate: string
  endDate: string
  status: FiscalPeriodStatus
  closedAt: string | null
  /** Journal entries dated in this period (one grouped count server-side). */
  entryCount: number
}

export interface FiscalYear {
  id: string
  label: string
  startDate: string
  endDate: string
  status: FiscalPeriodStatus
  periods: FiscalPeriod[]
}

export interface FiscalCalendar {
  fiscalYearStartMonth: number
  startMonthEditable: boolean
  /** Whether this caller may close or reopen a period (the server's ClosePeriod gate). */
  canClosePeriods: boolean
  years: FiscalYear[]
}

export interface FiscalScopeInput {
  workspaceId: string
}

/* --- Opening balances (IBA-71) --- */

export interface OpeningFundOption {
  id: string
  /** Display name, already in the reader's language (ADR-0030). */
  name: string
  /** The linked income account's i18n key. Informational only; null for a CUSTOM fund. */
  nameTranslationKey?: string | null
  isDefault: boolean
}

/** Per-account opening status: `set` accounts are read-only with their amount/fund; unset ones are still settable. */
export interface OpeningAccountRow {
  id: string
  name: string
  set: boolean
  openingBalance: MoneyText | null
  fundId: string | null
  fundName: string | null
}

export interface OpeningBalancesState {
  branchId: string
  baseCurrency: string
  /** The shared opening date once any account is set; null before finance is initialized. */
  openingDate: string | null
  initialized: boolean
  funds: OpeningFundOption[]
  accounts: OpeningAccountRow[]
}

export interface OpeningBalancesQuery {
  workspaceId: string
  branchId?: string | null
  scope?: FinanceCategoryScope
}

/* --- Account reconciliation (IBA-73) --- */

/**
 * Reconciliation lifecycle (IBA-161). DRAFT → PENDING_REVIEW → APPROVED | REJECTED, both terminal.
 * Mirrors the Prisma enum; the legal transitions live in `@ibada/shared-types/finance`.
 */
export enum ReconciliationSessionStatus {
  Draft = "DRAFT",
  PendingReview = "PENDING_REVIEW",
  Approved = "APPROVED",
  Rejected = "REJECTED",
}

/**
 * The lifecycle actions the server says this viewer may perform right now.
 *
 * Values match `ReconciliationTransitionAction` in `@ibada/shared-types/finance`. The client renders what the
 * server sends and never derives its own — a UI that guesses eventually offers a button the API refuses, and
 * on a review queue that reads as the system having lost the submission.
 */
export enum ReconciliationViewerAction {
  Submit = "submit",
  Approve = "approve",
  Reject = "reject",
  WithdrawToDraft = "withdraw_to_draft",
}

export interface ReconciliationSessionSummary {
  id: string
  financeAccount: { id: string; name: string }
  statementDateFrom: string
  statementDateTo: string
  /** The scale the three balances below are denominated at (IBA-116) — null only if the API omitted it. */
  currency: CurrencyCode | null
  /**
   * Null when branch autonomy withholds this branch's figures (ADR-0024).
   *
   * WHICH accounts were reconciled and WHEN is the branch's compliance record and the parish may read it; the
   * balances are the branch's money and it may not. Null rather than zero — `0.00` is what made a branch
   * holding money indistinguishable from one holding none — and rather than a share, because a balance has no
   * group total to be a fraction of.
   */
  statementBalance: MoneyText | null
  systemBalance: MoneyText | null
  difference: MoneyText | null
  status: ReconciliationSessionStatus
  linesCount: number
}

export interface ReconciliationLineTransaction {
  id: string
  transactionReference: string | null
  type: string
  amount: MoneyText
  occurredAt: string
  description: string | null
  status: string
  /** Carried so the inline metadata edit prefills; saving a blank form would otherwise clear these. */
  paymentMethod: FinancePaymentMethod | null
  paymentReference: string | null
}

/**
 * What the preparer decided about one transaction. A boolean could not carry this: "not cleared" conflated
 * "not yet looked at" with "looked at and deliberately set aside".
 */
export enum ReconciliationLineState {
  Outstanding = "OUTSTANDING",
  Cleared = "CLEARED",
  Excluded = "EXCLUDED",
  Investigating = "INVESTIGATING",
}

export interface ReconciliationLine {
  id: string
  state: ReconciliationLineState
  /** Why it was excluded or flagged — required by the API for those two states. */
  stateNote: string | null
  clearedAt: string | null
  clearedByUserId: string | null
  transaction: ReconciliationLineTransaction
}

export interface ReconciliationSessionDetail {
  id: string
  workspaceId: string
  branchId: string
  financeAccountId: string
  financeAccount: { id: string; name: string }
  statementDateFrom: string
  statementDateTo: string
  /** The scale every money string on this session is denominated at (IBA-116) — the line amounts included. */
  currency: CurrencyCode | null
  statementBalance: MoneyText
  /** What the statement said at the START of the period; null when it was never captured. */
  statementOpeningBalance: MoneyText | null
  /** The opening + movement = closing identity, when an opening balance exists. */
  openingCheck: ReconciliationOpeningCheck | null
  systemBalance: MoneyText
  difference: MoneyText
  status: ReconciliationSessionStatus
  /** Whether lines and figures may still change — server-owned, so no surface re-derives "is it draft". */
  isEditable: boolean
  viewerActions: ReconciliationViewerAction[]
  createdAt: string
  createdByUserId: string | null
  submittedByUserId: string | null
  submittedAt: string | null
  /** Mandatory when the submitted difference was non-zero. */
  submissionNote: string | null
  reviewedByUserId: string | null
  reviewedAt: string | null
  /** Optional on approve, mandatory on reject. */
  decisionNote: string | null
  /** Who did what, when, and what they said — every transition, oldest first. */
  timeline: ReconciliationTimelineEvent[]
  /** The statement(s) this claim was prepared against. Evidence a reviewer can open — never parsed. */
  proofFiles: ReconciliationProofFile[]
  lines: ReconciliationLine[]
}

/** One lifecycle event, from the finance audit trail (so withdrawals and repeat submissions both survive). */
export interface ReconciliationTimelineEvent {
  id: string
  /** The `FinanceAuditAction` string, e.g. `RECONCILIATION_SUBMITTED`. */
  action: string
  at: string
  actorUserId: string | null
  actorName: string | null
  actorAvatarUrl: string | null
  note: string | null
}

/**
 * Which period a disagreement started in. If the OPENINGS differ the fault predates this reconciliation and
 * re-checking its transactions is wasted effort; if the openings match but the movements do not, the error is
 * genuinely inside this period.
 */
export interface ReconciliationOpeningCheck {
  statementOpeningBalance: MoneyText
  systemOpeningBalance: MoneyText
  openingDifference: MoneyText
  statementMovement: MoneyText
  systemMovement: MoneyText
  movementDifference: MoneyText
  openingMatches: boolean
  movementMatches: boolean
}

export interface ReconciliationProofFile {
  id: string
  url: string
  fileName: string
  mimeType: string
  fileSize: number
  uploadedAt: string
}

export interface ReconciliationSessionListResponse {
  items: ReconciliationSessionSummary[]
  /** The scope's UNFILTERED review-queue depth — drives the Finance nav badge (ADR-0011: server-owned). */
  pendingReviewCount: number
}

export interface ListReconciliationSessionsQuery {
  workspaceId: string
  branchId?: string | null
  scope?: FinanceCategoryScope
  financeAccountId?: string | null
  status?: ReconciliationSessionStatus
  /** Sessions whose statement period OVERLAPS this window. */
  dateFrom?: string
  dateTo?: string
}

/** A lifecycle transition. `note` is required by the SERVER for rejections and unexplained differences. */
export interface ReconciliationTransitionInput {
  workspaceId: string
  sessionId: string
  note?: string
}

export interface CreateReconciliationSessionInput {
  workspaceId: string
  branchId?: string | null
  scope?: FinanceCategoryScope
  financeAccountId: string
  statementDateFrom: string
  statementDateTo: string
  /** Optional: not every statement shows one, and a fabricated opening is worse than none. */
  statementOpeningBalance?: MoneyText
  /** A canonical decimal string (IBA-116). May be zero or NEGATIVE — an overdrawn statement is a real statement. */
  statementBalance: MoneyText
}
